import { useEffect, useState } from "react";
import { adminOrders, adminOrder, adminRefund } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { extractErrorMessage } from "../../lib/errors";

const STATUS_COLOR = {
  paid: "bg-[#37B160] text-white",
  pending: "bg-amber-100 text-amber-900",
  partially_refunded: "bg-orange-100 text-orange-900",
  fully_refunded: "bg-red-100 text-red-900",
  cancelled: "bg-zinc-200 text-zinc-700",
};

export default function AdminOrders() {
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null); // selected order
  const [refundAmount, setRefundAmount] = useState("");

  const load = async () => {
    try {
      const items = await adminOrders(q);
      setOrders(items);
    } catch { toast.error("Could not load orders"); }
  };
  useEffect(() => { load(); }, []);

  const openOrder = async (id) => {
    try {
      const o = await adminOrder(id);
      setOpen(o);
      const remaining = (o.amount_total - (o.amount_refunded || 0)).toFixed(2);
      setRefundAmount(remaining);
    } catch { toast.error("Could not open order"); }
  };

  const onRefund = async () => {
    if (!open) return;
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) { toast.error("Enter a refund amount"); return; }
    try {
      await adminRefund(open.order_id, { amount: amt });
      toast.success("Refund issued via Stripe");
      setOpen(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Refund failed"));
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <h1 className="text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Orders</h1>
      <p className="text-sm text-[#4B5563]">All transactions. Click an order to view details and issue refunds.</p>
      <div className="mt-4 flex gap-2 max-w-md">
        <Input placeholder="Search name, email, order id…" value={q} onChange={e => setQ(e.target.value)} data-testid="orders-search" />
        <Button onClick={load} variant="outline" className="border-black/15" data-testid="orders-search-btn">Search</Button>
      </div>

      <div className="mt-6 bg-white border border-black/5 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-[#4B5563] text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Refunded</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.order_id} className="border-t border-black/5 hover:bg-black/[0.02]" data-testid={`order-row-${o.order_id}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{o.customer_name}</div>
                  <div className="text-xs text-[#9CA3AF]">{o.customer_email}</div>
                </td>
                <td className="px-4 py-3">£{o.amount_total?.toFixed(2)}</td>
                <td className="px-4 py-3">£{(o.amount_refunded || 0).toFixed(2)}</td>
                <td className="px-4 py-3"><Badge className={STATUS_COLOR[o.status] || "bg-black/5 text-black/70"}>{o.status.replaceAll("_"," ")}</Badge></td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">{new Date(o.created_at).toLocaleString("en-GB")}</td>
                <td className="px-4 py-3"><Button size="sm" variant="ghost" onClick={() => openOrder(o.order_id)} data-testid={`order-open-${o.order_id}`}>Open</Button></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-[#9CA3AF]">No orders yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-lg" data-testid="order-detail-modal">
          <DialogHeader><DialogTitle>Order {open?.order_id}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[#9CA3AF] text-xs uppercase tracking-[0.2em]">Customer</div>
                <div className="font-medium">{open.customer_name}</div>
                <div className="text-[#4B5563]">{open.customer_email} · {open.customer_phone}</div>
              </div>
              <div>
                <div className="text-[#9CA3AF] text-xs uppercase tracking-[0.2em]">Items</div>
                {open.items?.map((it, i) => (
                  <div key={i} className="mt-1">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-[#4B5563]">{it.transmission} · £{it.amount?.toFixed(2)}</div>
                    <ul className="mt-1 text-xs text-[#4B5563] space-y-0.5">
                      {it.slots?.map((s, j) => <li key={j}>· {s.date} at {s.time}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-[#9CA3AF] uppercase tracking-[0.2em]">Total</div><div className="font-medium">£{open.amount_total?.toFixed(2)}</div></div>
                <div><div className="text-xs text-[#9CA3AF] uppercase tracking-[0.2em]">Refunded</div><div className="font-medium">£{(open.amount_refunded || 0).toFixed(2)}</div></div>
                <div><div className="text-xs text-[#9CA3AF] uppercase tracking-[0.2em]">Stripe PI</div><div className="text-xs font-mono">{open.stripe_payment_intent || "—"}</div></div>
                <div><div className="text-xs text-[#9CA3AF] uppercase tracking-[0.2em]">Status</div><Badge className={STATUS_COLOR[open.status] || ""}>{open.status?.replaceAll("_"," ")}</Badge></div>
              </div>
              {open.status !== "fully_refunded" && open.stripe_payment_intent && (
                <div className="bg-[#F3F4F6] rounded-xl p-4">
                  <div className="text-xs text-[#9CA3AF] uppercase tracking-[0.2em] mb-2">Issue refund</div>
                  <div className="flex gap-2">
                    <Input value={refundAmount} onChange={e => setRefundAmount(e.target.value)} type="number" step="0.01" data-testid="refund-amount-input" />
                    <Button onClick={onRefund} className="bg-[#111827] text-white hover:bg-[#374151]" data-testid="refund-process-btn">Process refund</Button>
                  </div>
                  <div className="text-xs text-[#9CA3AF] mt-2">Up to £{(open.amount_total - (open.amount_refunded || 0)).toFixed(2)} available.</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)} className="border-black/15">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
